import type { Component } from 'vue';
import type { SettingsSection } from '#layers/global-settings/constants/settings-overlay-sections';
import { defineAsyncComponent } from 'vue';
import { featureFlagsVisible } from '#layers/base/public';
import { DEFAULT_SECTION, isSettingsSection } from '#layers/global-settings/constants/settings-overlay-sections';

// `Record<SettingsSection, …>` so adding a section without a panel is a compiler error.
const SETTINGS_PANELS = {
	'organization/extensions': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/OrganizationExtensions.vue')),
	'account/profile': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/AccountProfile.vue')),
	'account/access': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/AccountAccess.vue')),
	'organization/workspaces': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/OrganizationWorkspaces.vue')),
	'organization/members': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/OrganizationMembers.vue')),
	'organization/general': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/OrganizationGeneral.vue')),
	'organization/api-keys': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/OrganizationApiKeys.vue')),
	'organization/service-accounts': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/OrganizationServiceAccounts.vue')),
	'organization/sso': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/OrganizationSso.vue')),
	'organization/audit-logs': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/OrganizationAuditLogs.vue')),
	'organization/license': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/OrganizationLicense.vue')),
	'developer/feature-flags': defineAsyncComponent(() => import('#layers/global-settings/components/SettingsOverlay/panels/DeveloperFeatureFlags.vue')),
} as const satisfies Record<SettingsSection, Component>;

/** Sections reachable only while their gate is open, both from the sidebar and through a `?settings=` link. */
const SECTION_GATES: Partial<Record<SettingsSection, () => boolean>> = {
	'developer/feature-flags': () => featureFlagsVisible.value,
};

/** Whether `section` names a panel whose gate, if any, is open. Reactive on the gate. */
export function isSectionVisible(section: string): boolean {
	return isSettingsSection(section) && (SECTION_GATES[section]?.() ?? true);
}

/** Resolve a `?settings=` value to a panel; unknown, empty, or gated-off sections fall back to the default. */
export function panelFor(section: string | null) {
	if (!isSettingsSection(section) || !isSectionVisible(section)) return SETTINGS_PANELS[DEFAULT_SECTION];

	return SETTINGS_PANELS[section];
}
